import { ButtonBase, Typography, alpha, useTheme } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { GraphChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { ScoreProfiles } from 'features/augmented-writing/augmented-writing.types';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  contactScores?: ScoreProfiles;
  userScores?: ScoreProfiles;

  clickedChartCategoryKey?: string;

  onChartCategoryClicked?(category: string | string[]): void;
}

const MotiveChart: React.FC<Props> = ({ contactScores, userScores, clickedChartCategoryKey, onChartCategoryClicked }) => {
  echarts.use([GraphChart, CanvasRenderer]);
  const radius = 45;
  const { classes, cx } = useStyles({ radius });
  const { t } = useTranslation();
  const theme = useTheme();

  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    const dataArray = [];

    if (contactScores) {
      const contactData = {
        value: [
          contactScores.jointPosAffil * 100,
          contactScores.jointPosAchieve * 100,
          contactScores.regFocus * 100,
          contactScores.jointPosPower * 100,
        ],
        symbolSize: 0,
        areaStyle: {
          color: alpha(theme.palette.analysisSecondary.main, 0.35),
        },
      };
      dataArray.push(contactData);
    }

    if (userScores) {
      const userData = {
        value: [userScores.jointPosAffil * 100, userScores.jointPosAchieve * 100, userScores.regFocus * 100, userScores.jointPosPower * 100],
        symbolSize: 0,
        areaStyle: {
          color: alpha(theme.palette.primary.main, 0.35),
        },
      };
      dataArray.push(userData);
    }
    setData(dataArray);
  }, [contactScores, userScores]);

  const option = {
    color: [theme.palette.analysisSecondary.main, theme.palette.primary.main],
    radar: [
      {
        indicator: [
          { name: t('chartLabelAffil'), min: 0, max: 100 },
          { name: t('chartLabelAchieve'), min: 0, max: 100 },
          { name: t('chartLabelOrientation'), min: 0, max: 100 },
          { name: t('chartLabelPower'), min: 0, max: 100 },
        ],
        startAngle: 90,
        radius: radius,
        splitNumber: 4,
        shape: 'circle',
        axisName: {
          show: false,
        },
        axisLine: {
          lineStyle: {
            color: theme.palette.greys.light,
          },
        },
        splitLine: {
          lineStyle: {
            color: theme.palette.greys.light,
          },
        },
      },
    ],
    series: [
      {
        type: 'radar',
        silent: true,
        emphasis: {
          lineStyle: {
            width: 4,
          },
        },
        data: data,
      },
    ],
  };

  return (
    <div className={classes.chartContainer}>
      <div className={classes.innerChartContainer}>
        <ReactECharts echarts={echarts} option={option} notMerge={true} lazyUpdate={true} style={{ flex: 1, height: '100%', width: '100%' }} />
        <ButtonBase
          id={'focusAfterEditor'}
          className={cx(
            classes.motiveItemAffil,
            onChartCategoryClicked && userScores ? classes.clickable : null,
            clickedChartCategoryKey === 'jointPosAffil' && classes.clicked
          )}
          onClick={onChartCategoryClicked && userScores ? () => onChartCategoryClicked('jointPosAffil') : undefined}
          aria-label={`${t('highlightWordsWith')} ${t('chartLabelAffil')}`}
          disabled={_.isNil(onChartCategoryClicked)}
        >
          <Typography className={classes.motiveItemText} align={'center'}>
            {t('chartLabelAffil')}
          </Typography>
        </ButtonBase>
        <ButtonBase
          className={cx(
            classes.motiveItemPower,
            onChartCategoryClicked && userScores ? classes.clickable : null,
            clickedChartCategoryKey === 'jointPosPower' && classes.clicked
          )}
          onClick={onChartCategoryClicked && userScores ? () => onChartCategoryClicked('jointPosPower') : undefined}
          aria-label={`${t('highlightWordsWith')} ${t('chartLabelPower')}`}
          disabled={_.isNil(onChartCategoryClicked)}
        >
          <Typography className={classes.motiveItemText} align={'center'}>
            {t('chartLabelPower')}
          </Typography>
        </ButtonBase>
        <ButtonBase
          className={cx(
            classes.motiveItemReg,
            onChartCategoryClicked && userScores ? classes.clickable : null,
            clickedChartCategoryKey === 'regFocus' && classes.clicked
          )}
          onClick={onChartCategoryClicked && userScores ? () => onChartCategoryClicked('regFocus') : undefined}
          aria-label={`${t('highlightWordsWith')} ${t('chartLabelOrientation')}`}
          disabled={_.isNil(onChartCategoryClicked)}
        >
          <Typography className={classes.motiveItemText} align={'center'}>
            {t('chartLabelOrientation')}
          </Typography>
        </ButtonBase>
        <ButtonBase
          className={cx(
            classes.motiveItemAch,
            onChartCategoryClicked && userScores ? classes.clickable : null,
            clickedChartCategoryKey === 'jointPosAchieve' && classes.clicked
          )}
          onClick={onChartCategoryClicked && userScores ? () => onChartCategoryClicked('jointPosAchieve') : undefined}
          aria-label={`${t('highlightWordsWith')} ${t('chartLabelAchieve')}`}
          disabled={_.isNil(onChartCategoryClicked)}
        >
          <Typography className={classes.motiveItemText} align={'center'}>
            {t('chartLabelAchieve')}
          </Typography>
        </ButtonBase>
      </div>
    </div>
  );
};

const useStyles = makeStyles<{ radius: number }>()((theme, { radius }) => ({
  chartContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  innerChartContainer: {
    margin: `${radius / 2 - 8}px ${radius}px`,
    height: `calc(${radius * 2}px + 15px)`,
    width: `calc(${radius * 2}px + 15px)`,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.between('lg', 1350)]: {
      margin: `${radius / 2 - 8}px 0`,
    },
  },
  motiveItemAffil: {
    top: `-${radius / 2 - 8}px`,
    position: 'absolute',
  },
  motiveItemPower: {
    left: `${radius * 2 + 18}px`,
    position: 'absolute',
  },
  motiveItemReg: {
    bottom: `-${radius / 2 - 8}px`,
    position: 'absolute',
  },
  motiveItemAch: {
    right: `${radius * 2 + 18}px`,
    position: 'absolute',
  },
  motiveItemText: {
    fontSize: theme.typography.pxToRem(11),
  },
  clickable: {
    textDecoration: 'underline',
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  },
  clicked: {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
}));

export default MotiveChart;
